/**
 * Small form pieces shared by the search form and the settings screen.
 * Sentence case, 13–14px text, no uppercase eyebrows (DESIGN.md).
 */
import type { JSX, ReactNode } from 'react';
import { cn } from '@/lib/utils';

export function FieldError({ message, id }: { message?: string | null; id?: string }): JSX.Element | null {
  if (!message) return null;
  return (
    <p id={id} role="alert" className="text-xs text-destructive-strong">
      {message}
    </p>
  );
}

export function Field({
  label,
  help,
  cost,
  error,
  htmlFor,
  className,
  children,
}: {
  label: ReactNode;
  help?: string | null;
  cost?: string | null;
  error?: string | null;
  htmlFor?: string;
  className?: string;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className={cn('grid content-start gap-1.5', className)}>
      <label htmlFor={htmlFor} className="text-sm font-medium text-foreground/90">
        {label}
      </label>
      {children}
      {(help || cost) && (
        <p className="text-xs leading-relaxed text-muted-foreground">
          {help}
          {cost && <span className="text-pending-strong">{help ? ' · ' : ''}{cost}</span>}
        </p>
      )}
      <FieldError message={error} />
    </div>
  );
}

export function CheckboxField({
  id,
  checked,
  onChange,
  label,
  help,
  cost,
  error,
  disabled,
}: {
  id: string;
  checked: boolean;
  onChange: (v: boolean) => void;
  label: ReactNode;
  help?: string | null;
  cost?: string | null;
  error?: string | null;
  disabled?: boolean;
}): JSX.Element {
  return (
    <div className="grid gap-1">
      <label
        htmlFor={id}
        className={cn('flex items-start gap-2.5 text-sm', disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer')}
      >
        <input
          id={id}
          type="checkbox"
          checked={checked}
          disabled={disabled}
          onChange={(ev) => onChange(ev.target.checked)}
          className="mt-0.5 h-4 w-4 shrink-0 rounded border-input accent-[var(--brand)]"
        />
        <span className="min-w-0">
          <span className={checked ? 'font-medium text-foreground' : 'text-foreground/85'}>{label}</span>
          {(help || cost) && (
            <span className="mt-0.5 block text-xs leading-relaxed text-muted-foreground">
              {help}
              {cost && <span className="text-pending-strong">{help ? ' · ' : ''}{cost}</span>}
            </span>
          )}
        </span>
      </label>
      <FieldError message={error} />
    </div>
  );
}

/** A row of mutually exclusive choices (platform, mode). */
export function Segmented<T extends string>({
  value,
  onChange,
  options,
  label,
}: {
  value: T;
  onChange: (v: T) => void;
  options: { value: T; label: ReactNode; disabled?: boolean }[];
  label: string;
}): JSX.Element {
  return (
    <div role="radiogroup" aria-label={label} className="inline-flex h-9 max-w-full items-center gap-0.5 overflow-x-auto rounded-lg border border-input bg-[var(--input-bg)] p-0.5 shadow-control">
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          role="radio"
          aria-checked={value === o.value}
          disabled={o.disabled}
          onClick={() => onChange(o.value)}
          className={cn(
            'inline-flex h-full shrink-0 items-center gap-1.5 rounded-md px-3 text-sm font-medium transition-colors duration-150 ease-out-quart disabled:opacity-40',
            value === o.value ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-accent hover:text-foreground',
          )}
        >
          {o.label}
        </button>
      ))}
    </div>
  );
}

/** A count badge for collapsed group headers. */
export function CountBadge({ n }: { n: number }): JSX.Element | null {
  if (n <= 0) return null;
  return (
    <span className="inline-flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-brand/15 px-1.5 text-xs font-semibold tabular-nums text-brand">
      {n}
    </span>
  );
}

export function relativeTime(iso: string | null | undefined, now = Date.now()): string {
  if (!iso) return 'never';
  const t = new Date(iso).getTime();
  if (!Number.isFinite(t)) return 'never';
  const ms = t - now;
  const mins = Math.round(Math.abs(ms) / 60_000);
  const hours = Math.round(Math.abs(ms) / 3_600_000);
  const days = Math.round(Math.abs(ms) / 86_400_000);
  const unit = mins < 60 ? `${Math.max(mins, 1)}m` : hours < 24 ? `${hours}h` : `${days}d`;
  return ms >= 0 ? `in ${unit}` : `${unit} ago`;
}
